<script lang="ts">
  import { page } from "$app/stores";

  import clsx from "clsx";
  import type lottie from "lottie-web";
  import { onMount } from "svelte";
  import { Hamburger } from "svelte-hamburgers";

  import logoLottieSrc from "$lib/assets/logo.lottie";
  import logoLottieStillSrc from "$lib/assets/logo.lottie.png";
  import { isMobileMenuOpen } from "$lib/mobile-menu";
  import LoginSignupButtons from "$lib/ui/common/LoginSignupButtons.svelte";
  import MobileMenuDropdown from "$lib/ui/marketing/MobileMenuDropdown.svelte";
  import UseCaseDropdown from "$lib/ui/marketing/UseCaseDropdown.svelte";

  let useCaseDropdownOpen = false;
  let dropdownTimeout: ReturnType<typeof setTimeout> | null = null;

  const links = [
    { href: "/pricing", name: "Pricing" },
    { href: "/customers", name: "Customers" },
    { href: "/blog", name: "Blog" },
    { href: "/docs", name: "Docs" },
    { href: "/company", name: "Company", cls: "hidden lg:block" },
  ];

  function showUseCaseDropdown() {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
    }
    useCaseDropdownOpen = true;
  }

  function hideUseCaseDropdownDelayed() {
    dropdownTimeout = setTimeout(() => {
      useCaseDropdownOpen = false;
    }, 200);
  }

  let element: HTMLElement;

  let animationItem: ReturnType<typeof lottie.loadAnimation> | null = null;

  onMount(async () => {
    const { default: lottie } = await import("lottie-web");
    animationItem = lottie.loadAnimation({
      container: element,
      renderer: "svg",
      loop: false,
      autoplay: false,
      path: logoLottieSrc,
      initialSegment: [0, 60],
    });
    animationItem.setSpeed(1.3);
    animationItem.addEventListener("complete", () => {
      animationItem?.stop();
    });
  });
</script>

<div class="sticky top-0 md:pt-6 pt-4 z-10">
  <div class="marketing-container">
    <nav
      class="flex justify-between items-center bg-dark-gray p-2.5 relative transition-all duration-300"
      class:rounded-3xl={!useCaseDropdownOpen}
      class:rounded-t-xl={useCaseDropdownOpen}
    >
      <div class="ml-3">
        <a
          class="relative"
          href="/"
          on:click={() => isMobileMenuOpen.set(false)}
        >
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div
            class="h-[23px] w-[118px]"
            bind:this={element}
            on:mouseenter={() => {
              animationItem?.play();
            }}
          ></div>
          {#if !animationItem}
            <!--
            Display a still image before JavaScript is mounted so the Lottie
            logo does not appear blank. This can still flicker though.
            -->
            <img
              class="h-[22px] w-[118px] absolute top-0"
              src={logoLottieStillSrc}
              alt="Modal logo"
            />
          {/if}
        </a>
      </div>

      <div class="hidden md:flex items-center text-sm gap-7 xl:gap-10">
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
          on:mouseenter={showUseCaseDropdown}
          on:mouseleave={hideUseCaseDropdownDelayed}
          class="hover:text-brand-green cursor-default -m-3 p-3"
          class:text-brand-green={$page.url.pathname.startsWith("/use-cases")}
        >
          Use Cases
          {#if useCaseDropdownOpen}
            <UseCaseDropdown on:close={() => (useCaseDropdownOpen = false)} />
          {/if}
        </div>
        {#each links as { href, name, cls }}
          <a
            {href}
            class={clsx(
              "hover:text-brand-green",
              $page.url.pathname === href && "text-brand-green",
              cls,
            )}
          >
            {name}
          </a>
        {/each}
      </div>

      <div class="hidden md:flex items-center text-sm gap-7">
        <LoginSignupButtons />
      </div>

      <!-- Mobile animated hamburger menu -->
      <div class="mr-2 md:hidden text-light-green">
        <Hamburger
          bind:open={$isMobileMenuOpen}
          --color="currentColor"
          --layer-height="1.5px"
          --layer-width="18px"
          --layer-spacing="5px"
          --padding="4px"
        />
      </div>
    </nav>
  </div>

  <MobileMenuDropdown
    bind:open={$isMobileMenuOpen}
    {links}
    includeUseCases
    paddingTop="72px"
  />
</div>
